import { useCallback } from 'react';

import type {
  CreditCardType,
  FiatCurrency,
  TokenPaymentMethod,
} from '#src/__generated__/globalTypes.ts';
import { useConfigContext } from '#src/contexts/config/index.ts';
import { useCurrentUserCurrencies } from '#src/hooks/useCurrentUserCurrencies.tsx';

type PaymentMethodFeeContext = {
  paymentMethod: TokenPaymentMethod;
  creditCardType?: CreditCardType | null;
  currency?: FiatCurrency | null;
};

export type PaymentMethodFeeResolver = (
  context: PaymentMethodFeeContext
) => number;

type PaymentMethodFeeRate = {
  paymentMethod: TokenPaymentMethod;
  creditCardType: CreditCardType | null;
  currency: FiatCurrency | null;
  feeBasisPoints: number;
};

// Rows are served ordered by descending priority: take the first matching row.
// A null creditCardType/currency is a wildcard; no match means no fee.
export const resolvePaymentMethodFeeBasisPoints = (
  rates: readonly PaymentMethodFeeRate[],
  { paymentMethod, creditCardType, currency }: PaymentMethodFeeContext
): number => {
  const match = rates.find(
    rate =>
      rate.paymentMethod === paymentMethod &&
      (rate.creditCardType === null ||
        rate.creditCardType === creditCardType) &&
      (rate.currency === null || rate.currency === currency)
  );
  return match?.feeBasisPoints ?? 0;
};

const paymentFeesMayApply = (rates: readonly PaymentMethodFeeRate[]): boolean =>
  rates.some(rate => rate.feeBasisPoints > 0);

export const usePaymentFeesMayApply = (isSecondary: boolean) => {
  const { primaryMarketPaymentFeeRates, secondaryMarketPaymentFeeRates } =
    useConfigContext();

  return paymentFeesMayApply(
    isSecondary ? secondaryMarketPaymentFeeRates : primaryMarketPaymentFeeRates
  );
};

export const usePaymentMethodFeeBasisPoints = (isSecondary: boolean) => {
  const { primaryMarketPaymentFeeRates, secondaryMarketPaymentFeeRates } =
    useConfigContext();
  const { fiatCurrency } = useCurrentUserCurrencies();
  const rates = isSecondary
    ? secondaryMarketPaymentFeeRates
    : primaryMarketPaymentFeeRates;

  return useCallback(
    (context: PaymentMethodFeeContext) =>
      resolvePaymentMethodFeeBasisPoints(rates, {
        currency: fiatCurrency,
        ...context,
      }),
    [rates, fiatCurrency]
  );
};
